<template><markdown-wrapper><h1 id="notification">Notification <a class="header-anchor" href="#notification">&#128279;</a></h1>
<blockquote>
<p>Displays a global notification message at a corner of the page.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>Click on the button below to show a notification. By default, it is dismissible with a close button, and will dismiss automatically after 5000ms (both are configurable).</p>
<notification-example ref="notification-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Simplest Notification<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify2&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>No Auto-dismiss Notification<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-comment">// example with callback</span>
      <span class="hljs-comment">// pass a String as the notification content</span>
      <span class="hljs-title function_">notify</span> () {
        <span class="hljs-variable language_">this</span>.$notify(<span class="hljs-string">&#x27;This is a simple notify msg.&#x27;</span>, <span class="hljs-function">() =&gt;</span> {
          <span class="hljs-comment">// callback after dismissed</span>
          <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-string">&#x27;dismissed&#x27;</span>)
        })
      },
      <span class="hljs-comment">// example with Promise and options</span>
      <span class="hljs-title function_">notify2</span> () {
        <span class="hljs-variable language_">this</span>.$notify({
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Title&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;This notification will not dismiss automatically.&#x27;</span>,
          <span class="hljs-attr">duration</span>: <span class="hljs-number">0</span>
        }).<span class="hljs-title function_">then</span>(<span class="hljs-function">() =&gt;</span> {
          <span class="hljs-comment">// resolve after dismissed</span>
          <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-string">&#x27;dismissed&#x27;</span>)
        })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- notification-example.vue --&gt;</span>
</code></pre>
<h2 id="types">Types <a class="header-anchor" href="#types">&#128279;</a></h2>
<p>There're 4 optional types of notification: <code v-pre>info</code> / <code v-pre>success</code> / <code v-pre>warning</code> / <code v-pre>danger</code> (also alias as <code v-pre>error</code>). Except <code v-pre>type</code> option, you can also use registered shortcut methods (0.33.0+, see examples below).</p>
<p>Notification with specific type will has a default icon on the left, you can also change or remove the icon by <code v-pre>icon</code> option.</p>
<notification-types ref="notification-types"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;info&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span>&gt;</span>Info<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;success&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;success&quot;</span>&gt;</span>Success<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;warning&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span>&gt;</span>Warning<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;danger&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;danger&quot;</span>&gt;</span>Danger<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">info</span> () {
        <span class="hljs-variable language_">this</span>.$notify({
          <span class="hljs-attr">type</span>: <span class="hljs-string">&#x27;info&#x27;</span>,
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Heads up!&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;This alert needs your attention, but it\&#x27;s not super important.&#x27;</span>
        })
      },
      <span class="hljs-title function_">success</span> () {
        <span class="hljs-variable language_">this</span>.$notify({
          <span class="hljs-attr">type</span>: <span class="hljs-string">&#x27;success&#x27;</span>,
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Well done!&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;You successfully read this important alert message.&#x27;</span>
        })
      },
      <span class="hljs-title function_">warning</span> () {
        <span class="hljs-comment">// simple warning with content only</span>
        <span class="hljs-variable language_">this</span>.<span class="hljs-property">$notify</span>.<span class="hljs-title function_">warning</span>(<span class="hljs-string">&#x27;Better check yourself, you\&#x27;re not looking too good.&#x27;</span>)
      },
      <span class="hljs-title function_">danger</span> () {
        <span class="hljs-comment">// error msg with title and content (other options available too)</span>
        <span class="hljs-comment">// `error` is an alias of `danger` (both of them works)</span>
        <span class="hljs-variable language_">this</span>.<span class="hljs-property">$notify</span>.<span class="hljs-title function_">error</span>({
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Oh snap!&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;Change a few things up and try submitting again.&#x27;</span>
        })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- notification-types.vue --&gt;</span>
</code></pre>
<h2 id="placements">Placements <a class="header-anchor" href="#placements">&#128279;</a></h2>
<p>Notifications can be placed on any corner on a page.</p>
<p>The <code v-pre>position</code> prop defines which corner a notification will slide in. It can be <code v-pre>top-right</code> (default), <code v-pre>top-left</code>, <code v-pre>bottom-right</code> or <code v-pre>bottom-left</code>.</p>
<notification-placements ref="notification-placements"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify(&#x27;top-right&#x27;)&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Top Right (Default)<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify(&#x27;bottom-right&#x27;)&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Bottom Right<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify(&#x27;bottom-left&#x27;)&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Bottom Left<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify(&#x27;top-left&#x27;)&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Top Left<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">notify</span> (placement) {
        <span class="hljs-variable language_">this</span>.$notify({
          placement, <span class="hljs-comment">// equal to `placement: placement` in ES6</span>
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Title&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">`This is a notify msg at <span class="hljs-subst">${placement}</span>.`</span>
        })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- notification-placements.vue --&gt;</span>
</code></pre>
<h2 id="dismissible">Dismissible <a class="header-anchor" href="#dismissible">&#128279;</a></h2>
<p>By default a notification is dismissible with a close button, you can hide it by setting <code v-pre>dismissible</code> to <code v-pre>false</code>.</p>
<notification-without-dismiss-btn ref="notification-without-dismiss-btn"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;notify&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Notification Without Dismiss Button<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">notify</span> () {
        <span class="hljs-variable language_">this</span>.$notify({
          <span class="hljs-attr">title</span>: <span class="hljs-string">&#x27;Title&#x27;</span>,
          <span class="hljs-attr">content</span>: <span class="hljs-string">&#x27;This is a notification without dismiss btn.&#x27;</span>,
          <span class="hljs-attr">dismissible</span>: <span class="hljs-literal">false</span>
        })
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- notification-without-dismiss-btn.vue --&gt;</span>
</code></pre>
<h2 id="with-modals">With modals <a class="header-anchor" href="#with-modals">&#128279;</a></h2>
<p>By default, notifications will be covered by modal backdrops, you can fix this by adding below CSS into your project:</p>
<pre v-pre><code v-pre class="language-css"><span class="hljs-selector-tag">body</span> &gt; <span class="hljs-selector-class">.alert</span> {
  <span class="hljs-attribute">z-index</span>: <span class="hljs-number">2000</span>;
}
</code></pre>
<p>where <code v-pre>2000</code> can be any value that bigger than the modal z-index.</p>
<h2 id="global-method">Global method <a class="header-anchor" href="#global-method">&#128279;</a></h2>
<p><code v-pre>$notify(options, callback)</code> global method for <code v-pre>Vue.prototype</code> will be added <strong>if uiv is installed</strong>.</p>
<p>Note that the dismissed callback is optional.</p>
<p>The method will return a <code v-pre>Promise</code> object that resolve while the notification is dismissed (if supported by browser or with es6 promise polyfill).</p>
<h2 id="import-individually">Import individually <a class="header-anchor" href="#import-individually">&#128279;</a></h2>
<p>If you prefer importing <code v-pre>Notification</code> individually:</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-keyword">import</span> { <span class="hljs-title class_">Notification</span> } <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;uiv&#x27;</span>
</code></pre>
<p>The corresponding method is <code v-pre>Notification.notify</code>, with same parameters as above.</p>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="notification-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/services/notification/Notification.vue">Notification</a> <a class="header-anchor" href="#notification-1">&#128279;</a></h2>
<p>These props are used as <code v-pre>options</code> in the methods above.</p>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>title</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The notification title.</td>
</tr>
<tr>
<td><code v-pre>content</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The notification content.</td>
</tr>
<tr>
<td><code v-pre>html</code></td>
<td>Boolean</td>
<td><code v-pre>false</code></td>
<td></td>
<td>Allow HTML in content.</td>
</tr>
<tr>
<td><code v-pre>type</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Support: <code v-pre>info</code> / <code v-pre>success</code> / <code v-pre>warning</code> / <code v-pre>danger</code>.</td>
</tr>
<tr>
<td><code v-pre>duration</code></td>
<td>Number</td>
<td>5000</td>
<td></td>
<td>Dismiss after milliseconds, use 0 to prevent self-closing.</td>
</tr>
<tr>
<td><code v-pre>dismissible</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Show dismiss button.</td>
</tr>
<tr>
<td><code v-pre>placement</code></td>
<td>String</td>
<td>top-right</td>
<td></td>
<td>Support: <code v-pre>top-right</code> / <code v-pre>top-left</code> / <code v-pre>bottom-right</code> / <code v-pre>bottom-left</code>.</td>
</tr>
<tr>
<td><code v-pre>icon</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Custom icon class, use an empty string to disable icon.</td>
</tr>
<tr>
<td><code v-pre>customClass</code></td>
<td></td>
<td></td>
<td></td>
<td>Custom classes to alert, anything that can work with <code v-pre>v-bind:class</code> or <code v-pre>:class</code>.</td>
</tr>
<tr>
<td><code v-pre>offset</code></td>
<td>Number</td>
<td>15</td>
<td></td>
<td>The space in px between notifications.</td>
</tr>
<tr>
<td><code v-pre>offsetX</code></td>
<td>Number</td>
<td>15</td>
<td></td>
<td>The horizontal offset in px while displaying notification.</td>
</tr>
<tr>
<td><code v-pre>offsetY</code></td>
<td>Number</td>
<td>15</td>
<td></td>
<td>The vertical offset in px while displaying notification.</td>
</tr>
</tbody>
</table></div><h3 id="methods">Methods <a class="header-anchor" href="#methods">&#128279;</a></h3>
<p>You can call a method by <code v-pre>$notify</code> or <code v-pre>Notification</code>, for example <code v-pre>this.$notify.dismissAll()</code>.</p>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Params</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>dismissAll</code></td>
<td></td>
<td>Dismiss all notifications.</td>
</tr>
<tr>
<td><code v-pre>info</code> / <code v-pre>success</code> / <code v-pre>warning</code> / <code v-pre>danger</code> (<code v-pre>error</code>)</td>
<td>String or Object</td>
<td>Display a corresponding notification.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  

  

  

  
export default {components:{'notification-example':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <btn @click=\"notify\" type=\"primary\">Simplest Notification</btn>\n    <btn @click=\"notify2\" type=\"primary\">No Auto-dismiss Notification</btn>\n  </section>\n</div>",
    methods: {
      // example with callback
      // pass a String as the notification content
      notify () {
        this.$notify('This is a simple notify msg.', () => {
          // callback after dismissed
          console.log('dismissed')
        })
      },
      // example with Promise and options
      notify2 () {
        this.$notify({
          title: 'Title',
          content: 'This notification will not dismiss automatically.',
          duration: 0
        }).then(() => {
          // resolve after dismissed
          console.log('dismissed')
        })
      }
    }
  },'notification-types':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <btn @click=\"info\" type=\"info\">Info</btn>\n    <btn @click=\"success\" type=\"success\">Success</btn>\n    <btn @click=\"warning\" type=\"warning\">Warning</btn>\n    <btn @click=\"danger\" type=\"danger\">Danger</btn>\n  </section>\n</div>",
    methods: {
      info () {
        this.$notify({
          type: 'info',
          title: 'Heads up!',
          content: 'This alert needs your attention, but it\'s not super important.'
        })
      },
      success () {
        this.$notify({
          type: 'success',
          title: 'Well done!',
          content: 'You successfully read this important alert message.'
        })
      },
      warning () {
        // simple warning with content only
        this.$notify.warning('Better check yourself, you\'re not looking too good.')
      },
      danger () {
        // error msg with title and content (other options available too)
        // `error` is an alias of `danger` (both of them works)
        this.$notify.error({
          title: 'Oh snap!',
          content: 'Change a few things up and try submitting again.'
        })
      }
    }
  },'notification-placements':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <btn @click=\"notify('top-right')\" type=\"primary\">Top Right (Default)</btn>\n    <btn @click=\"notify('bottom-right')\" type=\"primary\">Bottom Right</btn>\n    <btn @click=\"notify('bottom-left')\" type=\"primary\">Bottom Left</btn>\n    <btn @click=\"notify('top-left')\" type=\"primary\">Top Left</btn>\n  </section>\n</div>",
    methods: {
      notify (placement) {
        this.$notify({
          placement, // equal to `placement: placement` in ES6
          title: 'Title',
          content: `This is a notify msg at ${placement}.`
        })
      }
    }
  },'notification-without-dismiss-btn':{"template":"<div class=\"markdown-live-example\">\n  <btn @click=\"notify\" type=\"primary\">Notification Without Dismiss Button</btn>\n</div>",
    methods: {
      notify () {
        this.$notify({
          title: 'Title',
          content: 'This is a notification without dismiss btn.',
          dismissible: false
        })
      }
    }
  }}}</script>